import { useEffect, useRef, useState } from 'react'

import { useApolloClient, useMutation } from '@apollo/client'
import { ActionIcon, Loader, Select, TextInput, Tooltip } from '@mantine/core'

import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_VOICE_SAMPLE_AUDIO,
  UPDATE_VOICE_AGENT,
} from 'src/graphql/voiceAgents.graphql'
import IconPlay from 'src/icons/IconPlay'
import { formatSnakeValue } from 'src/lib/formatters'
import { cn } from 'src/utils'

import SettingsSectionCard from '../../../components/SettingsSectionCard'
import { getVoiceLabel, VOICE_OPTIONS } from '../voiceOptions'

const GeneralCard = ({ voiceAgent, refetchVoiceAgents }) => {
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState({
    name: voiceAgent.name,
    voice: voiceAgent.voice,
  })
  const [sampleLoading, setSampleLoading] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const sampleCacheRef = useRef<Record<string, string>>({})

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues({ name: voiceAgent.name, voice: voiceAgent.voice })
    }
  }, [voiceAgent, editing])

  // Stop any playing sample when the card unmounts
  useEffect(() => {
    return () => {
      audioRef.current?.pause()
      audioRef.current = null
    }
  }, [])

  const [updateVoiceAgent, { loading }] = useMutation(UPDATE_VOICE_AGENT, {
    onCompleted: () => {
      toast('Voice agent updated successfully', 'success')
      setEditing(false)
      refetchVoiceAgents()
    },
    onError: (error) => {
      toast(`Failed to update voice agent: ${error.message}`, 'error')
    },
  })

  const apolloClient = useApolloClient()

  const playSample = async (voiceId: string) => {
    if (!voiceId || sampleLoading) return

    audioRef.current?.pause()
    audioRef.current = null

    try {
      let dataUri = sampleCacheRef.current[voiceId]
      if (!dataUri) {
        setSampleLoading(true)
        const { data } = await apolloClient.query({
          query: GET_VOICE_SAMPLE_AUDIO,
          variables: { voiceId },
          fetchPolicy: 'no-cache',
        })
        if (!data?.voiceSampleAudio) {
          throw new Error('No sample returned')
        }
        dataUri = data.voiceSampleAudio
        sampleCacheRef.current[voiceId] = dataUri
      }

      const audio = new Audio(dataUri)
      audioRef.current = audio
      await audio.play()
    } catch {
      toast('Could not play the voice sample. Please try again.', 'error')
    } finally {
      setSampleLoading(false)
    }
  }

  const voicePreviewButton = (voiceId: string) => (
    <Tooltip label="Play a sample of this voice">
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        disabled={!voiceId}
        onClick={() => playSample(voiceId)}
      >
        {sampleLoading ? (
          <Loader color="gray" size="xs" />
        ) : (
          <IconPlay className="h-4 w-4 fill-none stroke-doubleNickel-gray-600" />
        )}
      </ActionIcon>
    </Tooltip>
  )

  // Ensure the currently-stored voice always renders, even if it's a legacy
  // value that isn't part of the curated dropdown options.
  const voiceSelectData =
    values.voice && !VOICE_OPTIONS.some((o) => o.value === values.voice)
      ? [...VOICE_OPTIONS, { value: values.voice, label: values.voice }]
      : VOICE_OPTIONS

  const handleSave = () => {
    updateVoiceAgent({
      variables: {
        voiceAgentId: voiceAgent.voiceAgentId,
        input: { name: values.name, voice: values.voice },
      },
    })
  }

  const handleCancel = () => {
    setValues({ name: voiceAgent.name, voice: voiceAgent.voice })
    setEditing(false)
  }

  return (
    <SettingsSectionCard
      title="General"
      editable
      editing={editing}
      onEdit={() => setEditing(true)}
      onSave={handleSave}
      onCancel={handleCancel}
      saveDisabled={!values.name?.trim()}
      loading={loading}
    >
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <StyledReadOnlyInput
            label="Direction"
            value={formatSnakeValue(voiceAgent.direction)}
          />
        </div>

        <div className="col-span-3">
          {editing ? (
            <TextInput
              label="Name"
              value={values.name}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
              placeholder="Enter voice agent name"
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
              }}
              error={!values.name?.trim() ? 'Name is required' : undefined}
            />
          ) : (
            <StyledReadOnlyInput label="Name" value={voiceAgent.name} />
          )}
        </div>

        <div className="col-span-3">
          {editing ? (
            <div className="flex flex-row items-end gap-1">
              <Select
                label="Voice"
                value={values.voice}
                onChange={(value) =>
                  value && setValues({ ...values, voice: value })
                }
                data={voiceSelectData}
                allowDeselect={false}
                checkIconPosition="right"
                classNames={{
                  label: 'text-sm font-medium text-doubleNickel-gray-700',
                  root: 'flex-1',
                }}
              />
              <div className="pb-1">{voicePreviewButton(values.voice)}</div>
            </div>
          ) : (
            <StyledReadOnlyInput
              label="Voice"
              value={
                <span className="flex flex-row items-center gap-1">
                  {getVoiceLabel(voiceAgent.voice)}
                  {voicePreviewButton(voiceAgent.voice)}
                </span>
              }
            />
          )}
        </div>

        <div className="col-span-3">
          <StyledReadOnlyInput
            label="Status"
            value={
              <span
                className={cn(
                  voiceAgent.status === 'ACTIVE'
                    ? 'text-doubleNickel-success-500'
                    : 'text-doubleNickel-error-500'
                )}
              >
                {voiceAgent.status === 'ACTIVE' ? 'Active' : 'Inactive'}
              </span>
            }
          />
        </div>
      </div>
    </SettingsSectionCard>
  )
}

export default GeneralCard
